<template>
	<div
		:class="[
			$style['app-text-field-container'],
			{
				[$style['app-text-field-container--multiple']]: multiple
			}
		]"
	>
		<div
			class="input"
			:class="[
				$style['app-text-field'],
				$style['app-text-field--dense'],
				{
					[$style['app-text-field--invalid']]: invalid || maxCharacters && value.length > maxCharacters,
					[$style['app-text-field--disabled']]: isDisabled,
					[$style['app-text-field--with-search']]: search || leadingIcon,
					[$style['app-text-field--with-icon']]: !!icon,
					[$style['app-text-field--with-clear']]: (value || privateValue) && showClear,
					[$style['app-text-field--focused']]: isFocused,
					[$style['app-text-field--with-password-toggle']]: isPassword
				}
			]"
		>
			<input
				ref="input"
				v-bind="$attrs"
				class="input app-text-field-input"
				:class="[$style['app-text-field__input']]"
				:type="inputType"
				:value="value"
				:name="_uid"
				@focus="inputFocusHandler"
				@blur="inputBlurHandler"
				@input="inputInputHandler"
				@keydown="inputKeyDownHandler"
				@animationstart="onAutofillStart"
			/>
			<button
				v-if="isPassword"
				type="button"
				tabindex="-1"
				:class="$style['app-text-field__password-toggle']"
				@mousedown.prevent
				@click="isPasswordVisible = !isPasswordVisible"
			>
				<FontAwesomeIcon :icon="['far', isPasswordVisible ? 'eye' : 'eye-slash']" />
			</button>

			<FloatingLabel
				v-if="labelText"
				class="app-text-field-label"
				:class="[
					$style['app-text-field__label'],
					{
						[$style['app-text-field__label--with-search']] : (search || leadingIcon) && !isFloated
					}
				]"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<label
				v-if="!!!value"
				:class="[
					$style['app-text-field__placeholder'],
					{
						[$style['app-text-field__placeholder--indented']]: search || leadingIcon
					}
				]"
			>
				{{ placeholder }}
			</label>
			<label
				v-if="search"
				:class="$style['app-text-field__search']"
			>
				<FontAwesomeIcon :icon="['far', 'search']" />
			</label>
			<div
				v-else-if="leadingIcon"
				:class="$style['app-text-field__leading-icon']"
			>
				<FontAwesomeIcon
					size="xs"
					:icon="leadingIcon"
				/>
			</div>
			<label
				v-if="icon"
				:class="$style['app-text-field__icon']"
			>
				{{ icon }}
			</label>
			<NotchedOutline
				:class="$style['app-text-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-text-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<div
				v-if="(value || privateValue) && showClear"
				:class="$style['app-text-field__clear']"
				@click="clearInput"
			/>
		</div>
		<p
			v-if="helperText || maxCharacters"
			v-testid="'validation-message-element'"
			:class="$style['app-text-field-helper-text']"
		>
			<template v-if="helperText">
				{{ helperText }}
			</template>
			<span
				v-if="maxCharacters"
				:class="$style['app-text-field-helper-text--right']"
			>
				{{ value.length }} / {{ maxCharacters }}
			</span>
		</p>
	</div>
</template>


<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import requiredField from '@/mixins/requiredField'
import { sanitizeDecimal } from '@/utils/functions'

export default {
	name: 'BaseTextField',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel
	},
	mixins: [requiredField],
	inheritAttrs: false,
	props: {
		maxLength: {
			type: [String, Number],
			default: ''
		},
		leadingIcon: {
			type: [Array, Object],
			default: null
		},
		search: {
			type: Boolean,
			default: false
		},
		showClear: {
			type: Boolean,
			default: false
		},
		icon: {
			type: [String, Number],
			default: ''
		},
		placeholder: {
			type: [String, Number],
			default: ''
		},
		value: {
			type: [String, Number],
			default: ''
		},
		labelText: {
			type: String,
			default: ''
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		maxCharacters: {
			type: Number,
			default: 0
		},
		decimals: {
			type: Number,
			default: 2
		}
	},
	data: () => ({
		isFocused: false,
		isFloated: false,
		isAutofilled: false,
		privateValue: null,
		isPasswordVisible: false
	}),
	computed: {
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		},
		isPassword () {
			return this.$attrs.type === 'password'
		},
		inputType () {
			if (!this.isPassword) return this.$attrs.type
			return this.isPasswordVisible ? 'text' : 'password'
		}
	},
	watch: {
		placeholder: {
			immediate: true,
			handler () {
				if (this.placeholder) {
					this.isFloated = true
				}
			}
		},
		value: {
			immediate: true,
			handler () {
				if (this.value !== null && this.value !== '' || this.placeholder) {
					this.isFloated = true
				} else if (!this.isAutofilled) {
					this.isFloated = this.isFocused
				}
			}
		}
	},
	mounted () {
		this.__autofillPollTimer = null
		this.$nextTick(() => {
			const checkAutofill = (attempts = 0) => {
				const el = this.$refs.input
				if (!el) return

				const isAutofilledByBrowser = this.inputMatchesAutofill(el)
				const hasUnsyncedValue = el.value && el.value !== `${this.value}`

				if (hasUnsyncedValue && !this.isFocused) {
					this.isAutofilled = isAutofilledByBrowser
					this.isFloated = true
					this.$emit('input', el.value)
				}

				const shouldStop = this.isFocused || isAutofilledByBrowser || attempts >= 30
				if (!shouldStop) {
					this.__autofillPollTimer = setTimeout(() => checkAutofill(attempts + 1), 100)
				}
			}
			this.__autofillPollTimer = setTimeout(() => checkAutofill(), 100)
		})
	},
	beforeDestroy () {
		clearTimeout(this.__autofillPollTimer)
	},
	methods: {
		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},
		clearInput () {
			this.privateValue = ''
			this.isAutofilled = false
			this.$emit('input', '')
		},
		inputBlurHandler (event) {
			if (!this.isFocused) return
			this.isFocused = false
			this.isFloated = !!(this.value || this.placeholder)

			let finalValue = typeof this.privateValue === 'string' ? this.privateValue.trim() : this.value

			if (!finalValue) {
				this.isAutofilled = false
			}

			this.$emit('blur', event)
			this.$emit('input', finalValue)
		},
		inputInputHandler (e) {
			this.isFocused = true
			this.isFloated = true
			this.isAutofilled = false
			this.privateValue = e.target.value

			// Keep existing integer check if used without currency
			if (this.$attrs.type && this.$attrs.type === 'integer') {
				this.privateValue = this.privateValue.replace(/\D/g, '')
			}

			if (this.$attrs.type && this.$attrs.type === 'decimal') {
				this.privateValue = sanitizeDecimal(this.privateValue, this.decimals)
				if (this.$refs.input && this.$refs.input.value !== this.privateValue) {
					this.$refs.input.value = this.privateValue
				}
			}

			if (this.maxLength && this.privateValue.length > this.maxLength) {
				this.privateValue = this.privateValue.slice(0, this.maxLength)
			}

			this.$emit('input', this.privateValue)
		},
		inputMatchesAutofill (el) {
			if (!el || !el.matches) return false
			try {
				if (el.matches(':autofill')) return true
			} catch (e) {}
			try {
				if (el.matches(':-webkit-autofill')) return true
			} catch (e) {}
			return false
		},
		onAutofillStart (event) {
			if (event.animationName === 'autofill-start') {
				this.isAutofilled = true
				this.isFloated = true
				this.$nextTick(() => {
					const val = this.$refs.input && this.$refs.input.value
					if (val && val !== this.value) {
						this.$emit('input', val)
					}
				})
			}
		},
		inputKeyDownHandler (event) {
			if (event.keyCode === 13) {
				this.$emit('keydown', event)
				this.$refs.input.blur()
				return
			}
			if (this.maxCharacters && event.target.value.length >= this.maxCharacters) {
				if ([46, 8, 9, 27, 13, 110, 190].includes(event.keyCode) ||
					((event.keyCode === 65 || event.keyCode === 86 || event.keyCode === 67) &&
						(event.ctrlKey === true || event.metaKey === true)) ||
					(event.keyCode >= 35 && event.keyCode <= 40))
				{
					return
				}
				if (event.keyCode >= 48 && event.keyCode <= 57 ||
					event.keyCode >= 65 && event.keyCode <= 90) {
					event.preventDefault()
					return false
				}
			}
			this.$emit('keydown', event)
		}
	}
}
</script>
<style lang="scss" module>
.app-text-field-container {
    position: relative;
    text-overflow: ellipsis;
    width: 100%;

    &--multiple {
        padding: 0 $gutter * 1.25;
    }
}

.app-text-field-helper-text {
    display: flex;
    align-items: center;
    margin: 0;
    margin-bottom: calc($gutter / 2);
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
    width: 100%;
    position: relative;

    &--right {
        text-align: right;
        justify-self: flex-end;
        flex: 1;
    }
}

.app-text-field {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    margin: $gutter * 1.25 0;
    height: 56px;
    border: none;

    &:not(.app-text-field--disabled, .app-text-field--focused):hover {
        .app-text-field__outline {
            border-color: getColor(text);
        }

        .app-text-field__idle {
            border-color: getColor(text);
        }
    }

    &__label {
        z-index: 1;
    }

    &__clear {
        z-index: 2;
        height: 100%;
        width: 35px;
        position: absolute;
        right: 0;
        cursor: pointer;
        color: getColor(grey);

        // Use mask for theme-adaptive icon
        &::before {
            content: "";
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            width: 20px;
            height: 20px;
            background-color: currentcolor;
            mask-image: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 2 24 24"><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/></svg>');
            mask-size: contain;
            mask-repeat: no-repeat;
            mask-position: center;
        }

        &:hover {
            cursor: pointer;
            opacity: 0.7;
        }
    }

    &__icon {
        z-index: 1;
        font-size: 14px;
        line-height: 1.2;
        font-weight: 400;
        letter-spacing: 0.0094em;
        position: absolute;
        bottom: 16px;
        right: 20px;
        left: auto;
        white-space: nowrap;
        cursor: text;
        overflow: visible;
        color: getColor(text);
    }

    &__leading-icon {
        z-index: 2;
        opacity: 0.6;
        height: 100%;
        width: 35px;
        color: getColor(text);
        position: absolute;
        left: 15px;
        top: 13px;
    }

    &__search {
        z-index: 2;
        opacity: 0.6;
        height: 100%;
        width: 35px;
        position: absolute;
        left: 15px;
        top: 13px;
    }

    &__placeholder {
        z-index: 1;
        font-size: 0.813rem;
        line-height: 1.2;
        font-weight: 400;
        letter-spacing: 0.0094em;
        position: absolute;
        bottom: 16px;
        left: 20px;
        right: auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        cursor: text;
        overflow: hidden;
        color: getColor(helper-text);

        &--indented {
            left: 40px;
            right: 20px;
        }
    }

    &__input {
        z-index: 2;
        display: flex;
        box-sizing: content-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        width: 100%;
        height: 30px;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &:focus {
            outline: none;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
            box-shadow: 0 0 0 1000px transparent inset !important;
            -webkit-text-fill-color: getColor(text) !important;
            transition: background-color 5000s ease-in-out 0s;
            animation-name: autofill-start;
            animation-duration: 1ms;
        }

        &[type='number']::-webkit-inner-spin-button,
        &[type='number']::-webkit-outer-spin-button {
            appearance: none;
            margin: 0;
        }
    }

    &:not(.app-text-field--disabled) {
        & .app-text-field__idle {
            background-color: getColor(input-background);
        }

        & .app-text-field__outline {
            background-color: getColor(input-background);
        }

        .app-text-field__input {
            color: getColor(text) !important;
        }
    }

    &--dense {
        height: 48px;

        .app-text-field__input {
            padding: $gutter $gutter * 2;
        }
    }

    &--with-search {
        .app-text-field__input {
            padding-left: 40px;
        }

        .app-text-field__label--with-search {
            left: 40px;
        }
    }

    &--with-icon {
        .app-text-field__input {
            padding-right: $gutter * 4.5;
        }

        .app-text-field__label {
            max-width: calc(100% - 70px);
        }

        .app-text-field__placeholder {
            max-width: calc(100% - 70px);
        }
    }

    &--with-icon.app-text-field--with-clear {
        .app-text-field__input {
            padding-right: $gutter * 7;
        }

        .app-text-field__label {
            max-width: calc(100% - 100px);
        }

        .app-text-field__placeholder {
            max-width: calc(100% - 100px);
        }
    }

    &__password-toggle {
        z-index: 3;
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        background: transparent;
        color: getColor(text);
        opacity: 0.6;
        cursor: pointer;

        &:hover {
            opacity: 1;
        }
    }

    &--with-password-toggle {
        .app-text-field__input {
            padding-right: 40px;
        }

        .app-text-field__label,
        .app-text-field__placeholder {
            max-width: calc(100% - 60px);
        }

        .app-text-field__clear {
            right: 40px;
        }
    }

    &--no-helper {
        margin-bottom: $gutter * 1.25;
    }

    &--disabled {
        .app-text-field__idle {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__label {
        	color: getColor(input-label-disabled) !important;
        }

        .app-text-field__input {
            color: getColor(helper-text) !important;
        }
    }

    &--invalid {
        .app-text-field__input {
            caret-color: getColor(danger);
        }

        .app-text-field__outline {
            border-color: getColor(danger) !important;
        }

        .app-text-field__idle {
            border-color: getColor(danger) !important;
        }

        .app-text-field__label {
            color: getColor(danger) !important;
        }

        & ~ .app-text-field-helper-text {
            color: getColor(danger) !important;
        }
    }
}
</style>
<style>
@keyframes autofill-start { from { opacity: 1; } to { opacity: 1; } }

.app-text-field-input:autofill ~ .app-text-field-label.app-floating-label,
.app-text-field-input:-webkit-autofill ~ .app-text-field-label.app-floating-label,
.app-text-field-input:-webkit-autofill:hover ~ .app-text-field-label.app-floating-label,
.app-text-field-input:-webkit-autofill:focus ~ .app-text-field-label.app-floating-label {
    top: -7px;
    left: 20px;
    transform: scale(0.923);
    cursor: auto;
}
</style>
